import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Base_URL } from "../../url";
import "../../Styling/hrDashboard.css";
import { useAuth } from "../../Context/AuthContext";
import {
  ArrowLeft,
  Download,
  Search,
  Calendar,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Mail,
  User,
  Building,
  MapPin,
  Navigation,
  ChevronUp,
  ChevronDown,
  Edit3,
  Save,
  X,
  Thermometer,
  Coffee,
} from "lucide-react";

const AttendanceDetails = () => {
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // ✅ Check if a date is Sunday
  const isSunday = (dateString) => {
    if (!dateString) return false;
    try {
      const date = new Date(dateString);
      return date.getDay() === 0; // 0 = Sunday
    } catch {
      return false;
    }
  };

  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [attendanceData, setAttendanceData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [dateFilter, setDateFilter] = useState(getTodayDate());
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(getTodayDate());
  const [useDateRange, setUseDateRange] = useState(false);

  // Manual update states
  const [manualDate, setManualDate] = useState(getTodayDate());
  const [manualPunchInTime, setManualPunchInTime] = useState("");
  const [manualPunchOutTime, setManualPunchOutTime] = useState("");

  const [stats, setStats] = useState({
    total: 0,
    present: 0,
    absent: 0,
    late: 0,
    halfDay: 0,
    weekoff: 0,
  });

  // Modal states
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [manualStatus, setManualStatus] = useState("");
  const [updateMethod, setUpdateMethod] = useState("manual");

  // ✅ Format time for display
  const formatTime = (timeString) => {
    if (!timeString || timeString === "N/A") return "N/A";
    return timeString;
  };

  // ✅ Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  // ✅ Format coordinates for display
  const formatCoordinates = (coord) => {
    if (!coord || coord === "N/A") return "N/A";

    try {
      const coordValue = parseFloat(coord);
      return isNaN(coordValue) ? coord : coordValue.toFixed(6);
    } catch {
      return coord;
    }
  };

  // ✅ Get status badge - UPDATED VERSION
  const getStatusBadge = (record) => {
    // ✅ Sunday = Weekoff (highest priority)
    if (isSunday(record.attendanceDate)) {
      return {
        text: "Weekoff",
        color: "#8b5cf6",
        icon: <Calendar size={14} />,
        isLeave: false,
        leaveType: null,
        leaveFullType: null,
      };
    }

    // ✅ LEAVE EXISTS → SHOW STATUS AS PRESENT
    if (
      record.isLeave === true &&
      Array.isArray(record.leaveDetails) &&
      record.leaveDetails.length > 0
    ) {
      const leaveNames = record.leaveDetails
        .map((l) => l.leaveType)
        .filter(Boolean);

      const leaveFullType = leaveNames.join(" / ");

      const leaveTypeCodes = leaveNames
        .map((name) => {
          const upper = name.toUpperCase();
          if (upper.includes("SICK")) return "SL";
          if (upper.includes("EARNED")) return "EL";
          if (upper.includes("CASUAL")) return "CL";
          return "L";
        })
        .join(" / ");

      return {
        text: "Present", // ✅ FORCED
        color: "#10b981",
        icon: <CheckCircle size={14} />,
        isLeave: true, // ✅ REQUIRED
        leaveType: leaveTypeCodes,
        leaveFullType,
      };
    }

    // ✅ NORMAL ATTENDANCE (NO LEAVE → NO STATUS REASON)
    const status = record.attendanceStatus?.toLowerCase() || "absent";

    switch (status) {
      case "present":
        return {
          text: "Present",
          color: "#10b981",
          icon: <CheckCircle size={14} />,
          isLeave: false,
          leaveType: null,
          leaveFullType: null,
        };

      case "half day":
      case "halfday":
        return {
          text: "Half Day",
          color: "#f59e0b",
          icon: <Clock size={14} />,
          isLeave: false,
          leaveType: null,
          leaveFullType: null,
        };

      case "active":
        return {
          text: "Active",
          color: "#3b82f6",
          icon: <Clock size={14} />,
          isLeave: false,
          leaveType: null,
          leaveFullType: null,
        };

      case "absent":
        return {
          text: "Absent",
          color: "#ef4444",
          icon: <XCircle size={14} />,
          isLeave: false,
          leaveType: null,
          leaveFullType: null,
        };

      case "weekoff":
        return {
          text: "Weekoff",
          color: "#8b5cf6",
          icon: <Calendar size={14} />,
          isLeave: false,
          leaveType: null,
          leaveFullType: null,
        };

      default:
        return {
          text: status,
          color: "#6b7280",
          icon: <AlertCircle size={14} />,
          isLeave: false,
          leaveType: null,
          leaveFullType: null,
        };
    }
  };

  // ✅ Calculate manual working hours
  const calculateManualWorkingHours = (punchInTime, punchOutTime) => {
    if (!punchInTime || !punchOutTime) return "N/A";

    try {
      const [inHours, inMinutes] = punchInTime.split(":").map(Number);
      const [outHours, outMinutes] = punchOutTime.split(":").map(Number);

      const totalInMinutes = inHours * 60 + inMinutes;
      const totalOutMinutes = outHours * 60 + outMinutes;

      let totalMinutes = totalOutMinutes - totalInMinutes;

      // Handle overnight shifts
      if (totalMinutes < 0) {
        totalMinutes += 24 * 60;
      }

      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      return `${hours}h ${minutes}m (${totalMinutes} minutes)`;
    } catch (error) {
      console.error("Error calculating working hours:", error);
      return "N/A";
    }
  };

  // ✅ Calculate manual status based on working hours
  const getManualStatus = (punchInTime, punchOutTime) => {
    if (!punchInTime || !punchOutTime) return "Absent";

    try {
      const [inHours, inMinutes] = punchInTime.split(":").map(Number);
      const [outHours, outMinutes] = punchOutTime.split(":").map(Number);

      const totalInMinutes = inHours * 60 + inMinutes;
      const totalOutMinutes = outHours * 60 + outMinutes;

      let totalMinutes = totalOutMinutes - totalInMinutes;

      // Handle overnight shifts
      if (totalMinutes < 0) {
        totalMinutes += 24 * 60;
      }

      if (totalMinutes >= 480) {
        return "Present";
      } else if (totalMinutes >= 300 && totalMinutes < 480) {
        return "Half Day";
      } else {
        return "Absent";
      }
    } catch (error) {
      console.error("Error calculating manual status:", error);
      return "Absent";
    }
  };

  // ✅ Check if manual punch in is late
  const getIsLateArrival = (punchInTime) => {
    if (!punchInTime) return false;

    try {
      const [hours, minutes] = punchInTime.split(":").map(Number);

      if (hours > 9) return true;
      if (hours === 9 && minutes > 45) return true;

      return false;
    } catch (error) {
      console.error("Error checking late arrival:", error);
      return false;
    }
  };

  // ✅ Open location in maps
  const openInMaps = (latitude, longitude, address) => {
    if (isValidCoordinate(latitude) && isValidCoordinate(longitude)) {
      try {
        const lat = parseFloat(latitude);
        const lng = parseFloat(longitude);
        if (!isNaN(lat) && !isNaN(lng)) {
          const url = `https://www.google.com/maps?q=${lat},${lng}`;
          window.open(url, "_blank");
          return;
        }
      } catch (error) {
        console.error("Error parsing coordinates:", error);
      }
    }

    if (address && address !== "N/A") {
      const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
      window.open(url, "_blank");
    }
  };

  // ✅ Validate coordinates
  const isValidCoordinate = (coord) => {
    if (!coord || coord === "N/A") return false;
    try {
      const value = parseFloat(coord);
      return !isNaN(value) && value >= -90 && value <= 90;
    } catch {
      return false;
    }
  };

  // ✅ Handle column sorting
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      return () => {
        clearTimeout(handler);
      };
    }, [value, delay]);

    return debouncedValue;
  };

  const debouncedSearchTerm = useDebounce(searchTerm, 3000);

  // ✅ Sort data based on configuration
  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      switch (sortConfig.key) {
        case "user_ID":
          aValue = parseInt(aValue) || 0;
          bValue = parseInt(bValue) || 0;
          break;
        case "username":
        case "role":
        case "address":
        case "punchOutAddress":
        case "city":
        case "punchOutCity":
          aValue = (aValue || "").toString().toLowerCase();
          bValue = (bValue || "").toString().toLowerCase();
          break;
        case "attendanceDate":
          aValue = aValue ? new Date(aValue).getTime() : 0;
          bValue = bValue ? new Date(bValue).getTime() : 0;
          break;
        case "punchIn_Time":
        case "punchOut_Time":
          aValue = aValue || "99:99";
          bValue = bValue || "99:99";
          break;
        case "workingMinutes":
          aValue = aValue || 0;
          bValue = bValue || 0;
          break;
        case "attendanceStatus":
          aValue = (aValue || "").toString().toLowerCase();
          bValue = (bValue || "").toString().toLowerCase();
          break;
        case "latitude":
        case "longitude":
        case "punchOutLatitude":
        case "punchOutLongitude":
          aValue = aValue === "N/A" ? 999 : parseFloat(aValue) || 0;
          bValue = bValue === "N/A" ? 999 : parseFloat(bValue) || 0;
          break;
        default:
          aValue = aValue || "";
          bValue = bValue || "";
      }

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  };

  // ✅ Get sort icon for column header
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return (
        <div className="sort-icons">
          <ChevronUp size={12} className="sort-icon" />
          <ChevronDown size={12} className="sort-icon" />
        </div>
      );
    }

    return sortConfig.direction === "asc" ? (
      <ChevronUp size={14} className="sort-icon active" />
    ) : (
      <ChevronDown size={14} className="sort-icon active" />
    );
  };
  // ✅ Normalize date WITHOUT timezone shift
  const normalizeDate = (dateStr) => {
    if (!dateStr) return null;
    return dateStr.split("T")[0]; // YYYY-MM-DD
  };

  // ✅ Check leave status in bulk - FIXED VERSION
  const checkLeaveStatusInBulk = async (employeeIds, dates) => {
    try {
      console.log(
        `🔍 Checking leaves for ${employeeIds.length} employees on ${dates.length} dates...`,
      );

      if (employeeIds.length === 0 || dates.length === 0) {
        console.log("No employees or dates to check");
        return {};
      }

      // Build the URL
      const resourceIdsParam = employeeIds.join(",");
      const leaveDatesParam = dates.join(",");

      const apiUrl = `${Base_URL}/Leave/Attendance-status-slelcl/bulk-dates?resourceids=${encodeURIComponent(resourceIdsParam)}&leaveDates=${encodeURIComponent(leaveDatesParam)}`;

      console.log(`📤 API URL: ${apiUrl}`);

      const response = await fetch(apiUrl);

      if (response.ok) {
        const leaveResults = await response.json();
        console.log(`✅ Found ${leaveResults.length} leaves in API response`);

        // Create map for easy lookup: key = "employeeId_date"
        const leaveStatusMap = {};

        leaveResults.forEach((leave) => {
          const resourceId = String(leave.resourceId);
          const formattedDate = normalizeDate(leave.leaveDate);

          const key = `${resourceId}_${formattedDate}`;

          if (!leaveStatusMap[key]) {
            leaveStatusMap[key] = [];
          }

          leaveStatusMap[key].push({
            ...leave,
            formattedDate,
          });
        });

        console.log(
          `📊 Total leave entries in map: ${Object.keys(leaveStatusMap).length}`,
        );
        return leaveStatusMap;
      } else if (response.status === 404) {
        console.log("📭 No leaves found (404)");
        return {};
      } else {
        console.warn(`⚠️ API error: ${response.status}`);
        return {};
      }
    } catch (error) {
      console.error("🚨 Error in bulk leave check:", error);
      return {};
    }
  };

  const calculateStatistics = (data) => {
    const stats = {
      total: data.length,
      present: 0,
      absent: 0,
      late: 0,
      halfDay: 0,
      active: 0,
      weekoff: 0,
    };

    data.forEach((record) => {
      const status = record.attendanceStatus?.toLowerCase() || "absent";

      switch (status) {
       case "present":
case "active":
  stats.present++;
  break;

        case "absent":
          stats.absent++;
          break;
        case "half day":
        case "halfday":
          stats.halfDay++;
          break;
        // case "active":
        //   stats.active++;
        //   break;
        case "weekoff":
          stats.weekoff++;
          break;
      }

      if (record.isLateArrival) {
        stats.late++;
      }
    });

    return stats;
  };

  const processAttendanceDataWithLeaves = async (attendanceArray) => {
    console.log(
      `🔄 Processing ${attendanceArray.length} attendance records with leaves...`,
    );

    // Step 1: Basic processing
    const processedData = attendanceArray.map((record) => {
      const isSundayRecord = isSunday(record.attendanceDate);

      return {
        ...record,
        latitude: record.latitude || "N/A",
        longitude: record.longitude || "N/A",
        address: record.address || "N/A",
        city: record.city || "N/A",
        punchOutLatitude: record.punchOutLatitude || "N/A",
        punchOutLongitude: record.punchOutLongitude || "N/A",
        punchOutAddress: record.punchOutAddress || "N/A",
        punchOutCity: record.punchOutCity || "N/A",
        attendanceStatus: isSundayRecord
          ? "Weekoff"
          : record.attendanceStatus || "Absent",
        isSunday: isSundayRecord,
        isLeave: false,
        leaveType: null,
        leaveDetails: [],
      };
    });

    // Step 2: Get unique employee IDs and dates for leave check
    const nonSundayRecords = processedData.filter((record) => !record.isSunday);

    if (nonSundayRecords.length === 0) {
      console.log("📅 All records are Sunday (Weekoff)");
      return processedData;
    }

    const uniqueEmployeeIds = [
      ...new Set(nonSundayRecords.map((r) => String(r.user_ID))),
    ];
    const uniqueDates = [
      ...new Set(
        nonSundayRecords.map((record) => normalizeDate(record.attendanceDate)),
      ),
    ];

    console.log(
      `🔍 Unique Employees: ${uniqueEmployeeIds.length}, Unique Dates: ${uniqueDates.length}`,
    );

    const leaveStatusMap = await checkLeaveStatusInBulk(
      uniqueEmployeeIds,
      uniqueDates,
    );

    const finalData = processedData.map((record) => {
      // Sunday = Weekoff
      if (record.isSunday) {
        return record;
      }

      // Only Absent can become Leave
      if (record.isSunday) {
        return record;
      }

      const employeeId = String(record.user_ID);

      // ✅ FIXED DATE NORMALIZATION
      const attendanceDate = normalizeDate(record.attendanceDate);

      const key = `${employeeId}_${attendanceDate}`;
      const leaves = leaveStatusMap[key];

      if (leaves && leaves.length > 0) {
        const leaveShortCodes = leaves.map((l) => {
          const type = (l.leaveType || "").toLowerCase();

          if (type.includes("sick")) return "SL";
          if (type.includes("earned")) return "EL";
          if (type.includes("casual")) return "CL";

          return "L";
        });

        const totalLeaveDuration = leaves.reduce(
          (sum, l) => sum + (Number(l.leaveDuration) || 0),
          0,
        );

        return {
          ...record,
          isLeave: true,
          leaveType: leaveShortCodes.join(" / "),
          leaveDetails: leaves,
          leaveDuration: totalLeaveDuration, // ✅ NEW
          attendanceStatus: "Absent",
        };
      }

      return record;
    });

    // Debug: Count leaves
    const leaveCount = finalData.filter((r) => r.isLeave).length;
    const slCount = finalData.filter((r) => r.leaveType?.includes("SL")).length;
    const elCount = finalData.filter((r) => r.leaveType?.includes("EL")).length;
    const clCount = finalData.filter((r) => r.leaveType?.includes("CL")).length;

    console.log(`📊 FINAL STATISTICS:`);
    console.log(`   Total records: ${finalData.length}`);
    console.log(`   Leave records: ${leaveCount}`);
    console.log(`   SL (Sick Leave): ${slCount}`);
    console.log(`   EL (Earned Leave): ${elCount}`);
    console.log(`   CL (Casual Leave): ${clCount}`);

    return finalData;
  };

  // ✅ Fetch attendance data - FIXED VERSION
  const fetchAttendanceData = useCallback(
    async (searchQuery = "") => {
      if (!user?.organisationId) {
        setError("No organization selected");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      try {
        let url = "";

        if (useDateRange && startDate && endDate) {
          const params = new URLSearchParams({
            organisationId: user.organisationId,
            startDate: startDate,
            endDate: endDate,
          });

          if (searchQuery && searchQuery.trim() !== "") {
            params.append("searchTerm", searchQuery.trim());
          }

          url = `${Base_URL}/AttendanceReport/GetAttendanceSummaryByDateRange?${params.toString()}`;
        } else {
          url = `${Base_URL}/AttendanceReport/GetAttendanceSummary?organisationId=${user.organisationId}&date=${dateFilter}`;
        }

        console.log("🔍 Fetching attendance from:", url);

        const response = await fetch(url);

        if (!response.ok) {
          if (response.status === 404) {
            setAttendanceData([]);
            setFilteredData([]);
            setError("No attendance data found for the selected criteria");
            return;
          }
          throw new Error(
            `Failed to fetch attendance data: ${response.status} ${response.statusText}`,
          );
        }

        const data = await response.json();
        console.log("📊 Raw API Response:", data);

        const attendanceArray = Array.isArray(data.attendanceData)
          ? data.attendanceData
          : [];
        console.log(`📈 Total records from API: ${attendanceArray.length}`);

        // Process data with leave information
        const finalData =
          await processAttendanceDataWithLeaves(attendanceArray);

        // Update state
        setAttendanceData(finalData);
        setFilteredData(finalData);

        // Calculate stats
        const calculatedStats = calculateStatistics(finalData);
        setStats(calculatedStats);
      } catch (err) {
        console.error("❌ Error fetching attendance data:", err);
        setError(err.message || "Failed to fetch attendance data");
        setAttendanceData([]);
        setFilteredData([]);
      } finally {
        setLoading(false);
      }
    },
    [user?.organisationId, dateFilter, useDateRange, startDate, endDate],
  );

  // ✅ Apply filters and sorting
  useEffect(() => {
    console.log("🔄 Applying frontend filters...");

    let filtered = [...attendanceData];

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (record) =>
          (record.username?.toLowerCase() || "").includes(searchLower) ||
          (record.email?.toLowerCase() || "").includes(searchLower) ||
          (record.role?.toLowerCase() || "").includes(searchLower) ||
          (record.user_ID?.toString() || "").includes(searchTerm),
      );
    }

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((record) => {
        const status = record.attendanceStatus?.toLowerCase() || "absent";

        switch (statusFilter.toLowerCase()) {
          case "present":
  return status === "present" || status === "active";

          case "late":
            return record.isLateArrival;
          case "halfday":
            return status === "half day" || status === "halfday";
          case "absent":
            return status === "absent"; // This includes both regular absent and leave cases
          // case "active":
          //   return status === "active";
          case "weekoff":
            return status === "weekoff";
          default:
            return true;
        }
      });
    }

    // Apply sorting
    const sortedData = getSortedData(filtered);
    setFilteredData(sortedData);
  }, [attendanceData, searchTerm, statusFilter, sortConfig]);

  // ✅ Fetch data when date range changes
  useEffect(() => {
    if (user?.organisationId) {
      fetchAttendanceData(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, dateFilter, useDateRange, startDate, endDate]);

  // ✅ Handle stat card click - filter by status
  const handleStatCardClick = (status) => {
    if (status === "all") {
      setStatusFilter("all");
      setFilteredData(attendanceData);
    } else {
      setStatusFilter(status);
      const filtered = attendanceData.filter((record) => {
       if (status === "late") {
  return record.isLateArrival;
}

if (status === "present") {
  const s = record.attendanceStatus?.toLowerCase();
  return s === "present" || s === "active";
}

return record.attendanceStatus?.toLowerCase() === status.toLowerCase();

      });
      setFilteredData(filtered);
    }
  };

  // ✅ Mark Employee with Manual Times
  const markEmployeeWithManualTimes = async (
    employee,
    status,
    date,
    punchInTime,
    punchOutTime,
  ) => {
    if (!employee || !employee.user_ID || !punchInTime || !punchOutTime) {
      setError("Please fill all required fields");
      return;
    }

    const [inHours, inMinutes] = punchInTime.split(":").map(Number);
    const [outHours, outMinutes] = punchOutTime.split(":").map(Number);

    const totalInMinutes = inHours * 60 + inMinutes;
    const totalOutMinutes = outHours * 60 + outMinutes;

    if (totalOutMinutes <= totalInMinutes) {
      setError("Punch out time must be after punch in time");
      return;
    }

    setIsUpdating(true);
    try {
      const url = `${Base_URL}/AttendanceReport/UpdateAttendanceStatus`;

      const formattedPunchInTime = `${punchInTime}:00`;
      const formattedPunchOutTime = `${punchOutTime}:00`;

      const requestBody = {
        userId: employee.user_ID,
        organisationId: user.organisationId,
        date: date,
        status: status,
        punchInTime: formattedPunchInTime,
        punchOutTime: formattedPunchOutTime,
      };

      console.log("Sending manual attendance update:", requestBody);

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to update attendance: ${response.status} - ${errorText}`,
        );
      }

      const result = await response.json();
      console.log("Manual attendance update success:", result);

      await fetchAttendanceData();
      closeEmployeeModal();

      setError(
        `✅ Successfully updated ${employee.username}'s attendance for ${formatDate(date)} as ${status}`,
      );
      setTimeout(() => setError(""), 5000);
    } catch (err) {
      console.error("Error updating employee attendance manually:", err);
      setError(err.message || "Failed to update attendance. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  // ✅ Open Employee Detail Modal
  const openEmployeeModal = (employee, specificDate = null) => {
    setSelectedEmployee(employee);
    setManualStatus("");

    const modalDate = specificDate || employee.attendanceDate || getTodayDate();
    setManualDate(modalDate);

    setManualPunchInTime(employee.punchIn_Time || "");
    setManualPunchOutTime(employee.punchOut_Time || "");

    setManualStatus(employee.attendanceStatus || "");
    setUpdateMethod("manual");
    setShowEmployeeModal(true);
  };

  // ✅ Close Employee Detail Modal
  const closeEmployeeModal = () => {
    setSelectedEmployee(null);
    setManualStatus("");
    setManualDate(getTodayDate());
    setManualPunchInTime("");
    setManualPunchOutTime("");
    setUpdateMethod("manual");
    setShowEmployeeModal(false);
  };

  // ✅ Export to CSV - UPDATED
  const exportToCSV = () => {
    const headers = [
      "Employee ID",
      "Name",
      "Email",
      "Role",
      "Date",
      "Punch In",
      "Punch Out",
      "Working Hours",
      "Status",
      "Status Reason",
      "Leave Type",
      "Late Arrival",
      "Punch In Latitude",
      "Punch In Longitude",
      "Punch In Address",
      "Punch In City",
      "Punch Out Latitude",
      "Punch Out Longitude",
      "Punch Out Address",
      "Punch Out City",
    ];

    const csvData = filteredData.map((record) => {
      const status = getStatusBadge(record);
      const statusReason = status.isLeave
        ? status.leaveFullType || status.leaveType || "Leave"
        : "-";

      return [
        record.user_ID || "N/A",
        record.username || "N/A",
        record.email || "N/A",
        record.role || "N/A",
        formatDate(record.attendanceDate),
        formatTime(record.punchIn_Time),
        formatTime(record.punchOut_Time),
        record.workingHoursDisplay || "N/A",
        status.text,
        statusReason,
        status.leaveType || "-",
        record.isLateArrival ? "Yes" : "No",
        record.latitude || "N/A",
        record.longitude || "N/A",
        record.address || "N/A",
        record.city || "N/A",
        record.punchOutLatitude || "N/A",
        record.punchOutLongitude || "N/A",
        record.punchOutAddress || "N/A",
        record.punchOutCity || "N/A",
      ];
    });

    const csvContent = [
      headers.join(","),
      ...csvData.map((row) =>
        row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance-${user?.organisationId || "data"}-${dateFilter}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // ✅ Handle date range toggle
  const handleDateRangeToggle = () => {
    setUseDateRange(!useDateRange);
    if (!useDateRange) {
      const today = getTodayDate();
      setStartDate(today);
      setEndDate(today);
    } else {
      setStartDate(null);
      setEndDate(getTodayDate());
    }
  };

  // ✅ Get display date range text
  const getDateRangeDisplay = () => {
    if (useDateRange && startDate && endDate) {
      return `${formatDate(startDate)} to ${formatDate(endDate)}`;
    }
    return formatDate(dateFilter);
  };

  // Initial data fetch
  useEffect(() => {
    if (user?.organisationId) {
      fetchAttendanceData();
    } else {
      setError("No organization data provided");
      setLoading(false);
    }
  }, [fetchAttendanceData]);

  // Refresh data when date changes
  useEffect(() => {
    if (user?.organisationId) {
      fetchAttendanceData();
    }
  }, [dateFilter, useDateRange, startDate, endDate]);

  if (loading) {
    return (
      <div className="attendance-details-container">
        <div className="attendance-loading">
          <div className="loading-spinner"></div>
          <p>Loading attendance data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="attendance-details-container">
      {/* Employee Detail Modal */}
      {showEmployeeModal && selectedEmployee && (
        <div className="modal-overlay">
          <div className="modal-content employee-detail-modal">
            <div className="modal-header">
              <div className="modal-title-section">
                <h2>Employee Attendance Management</h2>
                <p>
                  Manually update attendance for {selectedEmployee.username}
                </p>
              </div>
              <button className="close-button" onClick={closeEmployeeModal}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="employee-info-card">
                <div className="employee-avatar">
                  <User size={32} />
                </div>
                <div className="employee-details">
                  <h3>{selectedEmployee.username}</h3>
                  <p className="employee-email">{selectedEmployee.email}</p>
                  <p className="employee-role">
                    Role: {selectedEmployee.role || "Not specified"}
                  </p>
                  <p className="employee-id">ID: #{selectedEmployee.user_ID}</p>
                </div>
              </div>

              <div className="manual-update-section">
                <h4>Manual Attendance Update</h4>
                <div className="update-description">
                  <p>
                    Use this section to manually update attendance for employees
                    who forgot to punch in/out.
                  </p>
                </div>

                <div className="manual-update-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Select Date *</label>
                      <input
                        type="date"
                        value={manualDate}
                        onChange={(e) => setManualDate(e.target.value)}
                        max={getTodayDate()}
                        className="form-input"
                        disabled={true}
                      />
                      <small className="date-locked-note">
                        📅 Date locked to{" "}
                        {formatDate(selectedEmployee.attendanceDate)}
                      </small>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Punch In Time *</label>
                      <input
                        type="time"
                        value={manualPunchInTime}
                        onChange={(e) => setManualPunchInTime(e.target.value)}
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label>Punch Out Time *</label>
                      <input
                        type="time"
                        value={manualPunchOutTime}
                        onChange={(e) => setManualPunchOutTime(e.target.value)}
                        className="form-input"
                      />
                    </div>
                  </div>

                  {manualPunchInTime && manualPunchOutTime && (
                    <div className="status-preview">
                      <h5>Status Preview</h5>
                      <div className="preview-details">
                        <p>
                          <strong>Working Hours:</strong>{" "}
                          {calculateManualWorkingHours(
                            manualPunchInTime,
                            manualPunchOutTime,
                          )}
                        </p>
                        <p>
                          <strong>Calculated Status:</strong>
                          <span
                            className={`suggested-status ${getManualStatus(manualPunchInTime, manualPunchOutTime).toLowerCase().replace(" ", "-")}`}
                          >
                            {getManualStatus(
                              manualPunchInTime,
                              manualPunchOutTime,
                            )}
                          </span>
                        </p>
                        {getIsLateArrival(manualPunchInTime) && (
                          <p>
                            <strong>Late Arrival:</strong>{" "}
                            <span className="late-indicator">Yes</span>
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="hr-actions-section">
                <h4>Update Attendance</h4>

                <div className="status-selection">
                  <label>Select Status to Apply:</label>
                  <div className="status-buttons">
                    <button
                      className={`status-btn present ${manualStatus === "Present" ? "active" : ""}`}
                      onClick={() => setManualStatus("Present")}
                      disabled={!manualPunchInTime || !manualPunchOutTime}
                    >
                      <CheckCircle size={16} />
                      Present
                    </button>
                    <button
                      className={`status-btn half-day ${manualStatus === "Half Day" ? "active" : ""}`}
                      onClick={() => setManualStatus("Half Day")}
                      disabled={!manualPunchInTime || !manualPunchOutTime}
                    >
                      <Clock size={16} />
                      Half Day
                    </button>
                    <button
                      className={`status-btn absent ${manualStatus === "Absent" ? "active" : ""}`}
                      onClick={() => setManualStatus("Absent")}
                      disabled={!manualPunchInTime || !manualPunchOutTime}
                    >
                      <XCircle size={16} />
                      Absent
                    </button>
                  </div>
                </div>

                <div className="action-buttons">
                  <button
                    className="mark-status-btn"
                    onClick={() =>
                      markEmployeeWithManualTimes(
                        selectedEmployee,
                        manualStatus,
                        manualDate,
                        manualPunchInTime,
                        manualPunchOutTime,
                      )
                    }
                    disabled={
                      isUpdating ||
                      !manualStatus ||
                      !manualPunchInTime ||
                      !manualPunchOutTime
                    }
                  >
                    {isUpdating ? (
                      <>
                        <div className="loading-spinner-small"></div>
                        Updating...
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        Update Attendance for{" "}
                        {formatDate(selectedEmployee.attendanceDate)} as{" "}
                        {manualStatus}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={closeEmployeeModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="attendance-header-card">
  {/* Left section */}
        <div className="attendance-header-left">
          {/* <button
            className="back-chip"
            onClick={() => navigate("/hr-dashboard")}
          >
            <ArrowLeft size={14} />
            Back
          </button> */}

          <div className="attendance-title">
            <h4>Attendance Management</h4>
            <p>
              Organization {user?.organisationId} • {getDateRangeDisplay()}
            </p>
          </div>
        </div>

  {/* Right actions */}
        <div className="attendance-header-actions">
          <button
            className="header-action-chip blue"
            onClick={() => navigate("/view-leaves")}
          >
            <Users size={14} />
            View Leaves
          </button>

          <button
            className="header-action-chip green"
            onClick={exportToCSV}
            disabled={filteredData.length === 0}
          >
            <Download size={14} />
            Export CSV
          </button>
        </div>
</div>


      {/* Stats Cards */}
      <div className="attendance-stats-grid">
  <div
    className={`attendance-stat-card total ${statusFilter === "all" ? "active" : ""}`}
    onClick={() => handleStatCardClick("all")}
  >
    <div className="stat-icon-box neutral">
      <Users size={16} />
    </div>
    <div className="stat-info">
      <span className="stat-value">{stats.total}</span>
      <span className="stat-title">Total Employees</span>
    </div>
  </div>

  <div
    className={`attendance-stat-card present ${statusFilter === "present" ? "active" : ""}`}
    onClick={() => handleStatCardClick("present")}
  >
    <div className="stat-icon-box present">
      <CheckCircle size={16} />
    </div>
    <div className="stat-info">
      <span className="stat-value">{stats.present}</span>
      <span className="stat-title">Present</span>
      <span className="stat-meta">8+ hours</span>
    </div>
  </div>

  <div
    className={`attendance-stat-card absent ${statusFilter === "absent" ? "active" : ""}`}
    onClick={() => handleStatCardClick("absent")}
  >
    <div className="stat-icon-box absent">
      <XCircle size={16} />
    </div>
    <div className="stat-info">
      <span className="stat-value">{stats.absent}</span>
      <span className="stat-title">Absent</span>
      <span className="stat-meta">Leave / No log</span>
    </div>
  </div>

  <div
    className={`attendance-stat-card late ${statusFilter === "late" ? "active" : ""}`}
    onClick={() => handleStatCardClick("late")}
  >
    <div className="stat-icon-box late">
      <Clock size={16} />
    </div>
    <div className="stat-info">
      <span className="stat-value">{stats.late}</span>
      <span className="stat-title">Late Arrivals</span>
      <span className="stat-meta">After 9:45 AM</span>
    </div>
  </div>

  <div
    className={`attendance-stat-card halfday ${statusFilter === "halfday" ? "active" : ""}`}
    onClick={() => handleStatCardClick("halfday")}
  >
    <div className="stat-icon-box halfday">
      <Clock size={16} />
    </div>
    <div className="stat-info">
      <span className="stat-value">{stats.halfDay}</span>
      <span className="stat-title">Half Day</span>
      <span className="stat-meta">5–8 hours</span>
    </div>
  </div>

  {/* <div
    className={`attendance-stat-card active-now ${statusFilter === "active" ? "active" : ""}`}
    onClick={() => handleStatCardClick("active")}
  >
    <div className="stat-icon-box active">
      <Clock size={16} />
    </div>
    <div className="stat-info">
      <span className="stat-value">{stats.active}</span>
      <span className="stat-title">Currently Active</span>
      <span className="stat-meta">Working now</span>
    </div>
  </div> */}
</div>


      {/* Filters */}
      <div className="attendance-filters-card">

{/* Search */}
<div className="filter-item search">
  <Search size={14} />
  <input
    type="text"
    placeholder="Search name, email, role, employee ID…"
    value={searchTerm}
    onChange={(e) => setSearchTerm(e.target.value)}
  />
</div>

{/* Status */}
<div className="filter-item">
  <select
    value={statusFilter}
    onChange={(e) => setStatusFilter(e.target.value)}
  >
    <option value="all">All Status</option>
    <option value="present">Present</option>
    <option value="late">Late</option>
    <option value="halfday">Half Day</option>
    <option value="absent">Absent</option>
    {/* <option value="active">Active</option> */}
    <option value="weekoff">Weekoff</option>
  </select>
</div>

{/* Date toggle */}
<div className="filter-item toggle">
  <label className="toggle-switch">
    <input
      type="checkbox"
      checked={useDateRange}
      onChange={handleDateRangeToggle}
    />
    <span className="slider" />
  </label>
  <span className="toggle-text">Date range</span>
</div>

{/* Date inputs wrapper (important for row alignment) */}
<div className="date-filters-row">
  {useDateRange ? (
    <>
      <div className="filter-item date">
        <Calendar size={14} />
        <input
          type="date"
          value={startDate || ""}
          onChange={(e) => setStartDate(e.target.value)}
          max={getTodayDate()}
        />
      </div>

      <div className="filter-item date">
        <Calendar size={14} />
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          min={startDate || ""}
          max={getTodayDate()}
        />
      </div>
    </>
  ) : (
    <div className="filter-item date">
      <Calendar size={14} />
      <input
        type="date"
        value={dateFilter}
        onChange={(e) => setDateFilter(e.target.value)}
        max={getTodayDate()}
      />
    </div>
  )}
</div>

{/* Clear */}
<button
  className="clear-filter-chip"
  onClick={() => {
    setSearchTerm("");
    setStatusFilter("all");
    setStartDate(null);
    setEndDate(getTodayDate());
    setDateFilter(getTodayDate());
    setUseDateRange(false);
    setSortConfig({ key: null, direction: "asc" });
  }}
>
  Clear
</button>
</div>



      {/* Results Info */}
      <div className="results-info">
        <span>
          Showing {filteredData.length} of {attendanceData.length} records
        </span>
        {searchTerm && <span className="search-term">for "{searchTerm}"</span>}
        {statusFilter !== "all" && (
          <span className="status-filter">with status: {statusFilter}</span>
        )}
        {sortConfig.key && (
          <span className="sort-info">
            Sorted by {sortConfig.key} (
            {sortConfig.direction === "asc" ? "A-Z" : "Z-A"})
          </span>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {/* Attendance Table */}
      <div className="attendance-table-container">
        {filteredData.length > 0 ? (
          <div className="table-scroll-wrapper">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th
                    className="sortable-header"
                    onClick={() => handleSort("user_ID")}
                  >
                    <div className="header-content">
                      Employee ID
                      {getSortIcon("user_ID")}
                    </div>
                  </th>
                  <th
                    className="sortable-header"
                    onClick={() => handleSort("username")}
                  >
                    <div className="header-content">
                      Employee Name
                      {getSortIcon("username")}
                    </div>
                  </th>
                  <th
                    className="sortable-header"
                    onClick={() => handleSort("role")}
                  >
                    <div className="header-content">
                      Role
                      {getSortIcon("role")}
                    </div>
                  </th>
                  <th
                    className="sortable-header"
                    onClick={() => handleSort("attendanceDate")}
                  >
                    <div className="header-content">
                      Date
                      {getSortIcon("attendanceDate")}
                    </div>
                  </th>
                  <th
                    className="sortable-header"
                    onClick={() => handleSort("punchIn_Time")}
                  >
                    <div className="header-content">
                      Punch In
                      {getSortIcon("punchIn_Time")}
                    </div>
                  </th>
                  <th
                    className="sortable-header"
                    onClick={() => handleSort("punchOut_Time")}
                  >
                    <div className="header-content">
                      Punch Out
                      {getSortIcon("punchOut_Time")}
                    </div>
                  </th>
                  <th
                    className="sortable-header"
                    onClick={() => handleSort("workingMinutes")}
                  >
                    <div className="header-content">
                      Working Hours
                      {getSortIcon("workingMinutes")}
                    </div>
                  </th>
                  <th>Status</th>
                  <th>Status Reason</th>
                  <th>Late</th>

                  {/* Location Columns */}
                  <th
                    className="sortable-header"
                    onClick={() => handleSort("latitude")}
                  >
                    <div className="header-content">
                      Punch In Latitude
                      {getSortIcon("latitude")}
                    </div>
                  </th>
                  <th
                    className="sortable-header"
                    onClick={() => handleSort("longitude")}
                  >
                    <div className="header-content">
                      Punch In Longitude
                      {getSortIcon("longitude")}
                    </div>
                  </th>
                  <th
                    className="sortable-header"
                    onClick={() => handleSort("address")}
                  >
                    <div className="header-content">
                      Punch In Address
                      {getSortIcon("address")}
                    </div>
                  </th>
                  <th
                    className="sortable-header"
                    onClick={() => handleSort("city")}
                  >
                    <div className="header-content">
                      Punch In City
                      {getSortIcon("city")}
                    </div>
                  </th>

                  <th
                    className="sortable-header"
                    onClick={() => handleSort("punchOutLatitude")}
                  >
                    <div className="header-content">
                      Punch Out Latitude
                      {getSortIcon("punchOutLatitude")}
                    </div>
                  </th>
                  <th
                    className="sortable-header"
                    onClick={() => handleSort("punchOutLongitude")}
                  >
                    <div className="header-content">
                      Punch Out Longitude
                      {getSortIcon("punchOutLongitude")}
                    </div>
                  </th>
                  <th
                    className="sortable-header"
                    onClick={() => handleSort("punchOutAddress")}
                  >
                    <div className="header-content">
                      Punch Out Address
                      {getSortIcon("punchOutAddress")}
                    </div>
                  </th>
                  <th
                    className="sortable-header"
                    onClick={() => handleSort("punchOutCity")}
                  >
                    <div className="header-content">
                      Punch Out City
                      {getSortIcon("punchOutCity")}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((record, index) => {
                  const status = getStatusBadge(record);
                  const hasValidPunchInCoordinates =
                    isValidCoordinate(record.latitude) &&
                    isValidCoordinate(record.longitude);
                  const hasValidPunchOutCoordinates =
                    isValidCoordinate(record.punchOutLatitude) &&
                    isValidCoordinate(record.punchOutLongitude);
                  const hasPunchInAddress =
                    record.address && record.address !== "N/A";
                  const hasPunchOutAddress =
                    record.punchOutAddress && record.punchOutAddress !== "N/A";
                  const isPunchInClickable =
                    hasValidPunchInCoordinates || hasPunchInAddress;
                  const isPunchOutClickable =
                    hasValidPunchOutCoordinates || hasPunchOutAddress;

                  // Get Status Reason - FIXED
                  let statusReason = "-";
                  if (status.isLeave) {
                    // Show full leave type from API
                    statusReason =
                      status.leaveFullType || status.leaveType || "Leave";
                  }

                  const uniqueKey = `${record.user_ID}-${record.attendanceDate}-${index}`;

                  return (
                    <tr
                      key={uniqueKey}
                      className="attendance-row"
                      data-leave={status.isLeave}
                    >
                      <td>
                        <div className="employee-id-cell">
                          <div className="employee-id-badge">
                            #{record.user_ID}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div
                          className="employee-info clickable"
                          onClick={() =>
                            openEmployeeModal(record, record.attendanceDate)
                          }
                          style={{ cursor: "pointer" }}
                        >
                          <div className="avatar">
                            <User size={16} />
                          </div>
                          <div className="employee-details">
                            <div className="employee-name">
                              {record.username || "N/A"}
                            </div>
                            <div className="employee-email">
                              {record.email || "N/A"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="role-badge">
                          {record.role || "N/A"}
                        </span>
                      </td>
                      <td>{formatDate(record.attendanceDate)}</td>
                      <td>
                        <div className="time-cell">
                          <Clock size={14} />
                          {formatTime(record.punchIn_Time)}
                        </div>
                      </td>
                      <td>
                        <div className="time-cell">
                          <Clock size={14} />
                          {formatTime(record.punchOut_Time)}
                        </div>
                      </td>
                      <td>
                        <div className="working-hours">
                          {record.workingHoursDisplay || "N/A"}
                        </div>
                      </td>
                      <td>
                        <div
                          className="attendance-status-badge"
                          style={{
                            backgroundColor: `${status.color}15`,
                            color: status.color,
                            borderColor: status.color,
                          }}
                          data-status={status.text}
                          data-leave={status.isLeave}
                        >
                          {status.icon}
                          {status.text}
                        </div>
                      </td>
                      <td>
                        <div className="status-reason-cell">
                          {status.isLeave && record.leaveDetails?.length > 0 ? (
                            <div className="leave-reason-badge">
                              {record.leaveDetails.map((leave, idx) => (
                                <span
                                  key={idx}
                                  className={`leave-type ${
                                    leave.leaveType
                                      ?.toLowerCase()
                                      .includes("sick")
                                      ? "sl"
                                      : leave.leaveType
                                            ?.toLowerCase()
                                            .includes("casual")
                                        ? "cl"
                                        : leave.leaveType
                                              ?.toLowerCase()
                                              .includes("earned")
                                          ? "el"
                                          : "l"
                                  }`}
                                >
                                  {leave.leaveType} ({leave.leaveDuration} Day
                                  {leave.leaveDuration > 1 ? "s" : ""})
                                  {idx < record.leaveDetails.length - 1 &&
                                    " / "}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="no-reason">No Leave</span>
                          )}
                        </div>
                      </td>

                      <td>
                        <div className="late-indicator">
                          {record.isLateArrival ? (
                            <span className="late-badge">Late</span>
                          ) : (
                            <span className="on-time">On Time</span>
                          )}
                        </div>
                      </td>

                      {/* Punch In Location */}
                      <td>
                        <div
                          className={`coordinate-cell latitude ${isPunchInClickable ? "clickable" : "not-clickable"}`}
                          onClick={
                            isPunchInClickable
                              ? () =>
                                  openInMaps(
                                    record.latitude,
                                    record.longitude,
                                    record.address,
                                  )
                              : undefined
                          }
                          title={
                            isPunchInClickable
                              ? "Click to open Punch In location"
                              : "No location data"
                          }
                        >
                          <MapPin size={12} />
                          {formatCoordinates(record.latitude)}
                        </div>
                      </td>
                      <td>
                        <div
                          className={`coordinate-cell longitude ${isPunchInClickable ? "clickable" : "not-clickable"}`}
                          onClick={
                            isPunchInClickable
                              ? () =>
                                  openInMaps(
                                    record.latitude,
                                    record.longitude,
                                    record.address,
                                  )
                              : undefined
                          }
                          title={
                            isPunchInClickable
                              ? "Click to open Punch In location"
                              : "No location data"
                          }
                        >
                          <MapPin size={12} />
                          {formatCoordinates(record.longitude)}
                        </div>
                      </td>
                      <td>
                        <div
                          className={`address-cell ${isPunchInClickable ? "clickable" : "not-clickable"}`}
                          onClick={
                            isPunchInClickable
                              ? () =>
                                  openInMaps(
                                    record.latitude,
                                    record.longitude,
                                    record.address,
                                  )
                              : undefined
                          }
                          title={
                            isPunchInClickable
                              ? "Click to open Punch In location"
                              : "No location data"
                          }
                        >
                          <Navigation size={12} />
                          <span className="address-text">
                            {record.address || "N/A"}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="city-cell">
                          <span className="city-text">
                            {record.city || "N/A"}
                          </span>
                        </div>
                      </td>

                      {/* Punch Out Location */}
                      <td>
                        <div
                          className={`coordinate-cell latitude ${isPunchOutClickable ? "clickable" : "not-clickable"}`}
                          onClick={
                            isPunchOutClickable
                              ? () =>
                                  openInMaps(
                                    record.punchOutLatitude,
                                    record.punchOutLongitude,
                                    record.punchOutAddress,
                                  )
                              : undefined
                          }
                          title={
                            isPunchOutClickable
                              ? "Click to open Punch Out location"
                              : "No location data"
                          }
                        >
                          <MapPin size={12} />
                          {formatCoordinates(record.punchOutLatitude)}
                        </div>
                      </td>
                      <td>
                        <div
                          className={`coordinate-cell longitude ${isPunchOutClickable ? "clickable" : "not-clickable"}`}
                          onClick={
                            isPunchOutClickable
                              ? () =>
                                  openInMaps(
                                    record.punchOutLatitude,
                                    record.punchOutLongitude,
                                    record.punchOutAddress,
                                  )
                              : undefined
                          }
                          title={
                            isPunchOutClickable
                              ? "Click to open Punch Out location"
                              : "No location data"
                          }
                        >
                          <MapPin size={12} />
                          {formatCoordinates(record.punchOutLongitude)}
                        </div>
                      </td>
                      <td>
                        <div
                          className={`address-cell ${isPunchOutClickable ? "clickable" : "not-clickable"}`}
                          onClick={
                            isPunchOutClickable
                              ? () =>
                                  openInMaps(
                                    record.punchOutLatitude,
                                    record.punchOutLongitude,
                                    record.punchOutAddress,
                                  )
                              : undefined
                          }
                          title={
                            isPunchOutClickable
                              ? "Click to open Punch Out location"
                              : "No location data"
                          }
                        >
                          <Navigation size={12} />
                          <span className="address-text">
                            {record.punchOutAddress || "N/A"}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="city-cell">
                          <span className="city-text">
                            {record.punchOutCity || "N/A"}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="no-data">
            <Users size={48} />
            <h3>No attendance records found</h3>
            <p>
              {searchTerm ||
              statusFilter !== "all" ||
              dateFilter !== getTodayDate() ||
              (useDateRange && startDate && endDate)
                ? "Try adjusting your filters to see more results"
                : "No attendance data available for the selected date"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendanceDetails;
